import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { defaultIfEmpty, map, switchMap, tap } from 'rxjs/operators';

/**
 * Routes a component's loads through one stream so only the latest request
 * counts.
 *
 * Each call to the returned function supersedes the load in flight: switchMap
 * unsubscribes from it, which aborts the HTTP request before its tap can write
 * stale data into the store. Whatever is in flight when the component is
 * destroyed is aborted the same way, so a response can't land on the next page.
 *
 * `loading` is told when a load starts and when the latest one ends. The API
 * maps failures to EMPTY, so completion — not an emission — marks the end; a
 * superseded load reports nothing, which leaves the flag up for its
 * replacement. (finalize would be wrong here: it also runs on supersession,
 * after the new load has already set the flag.)
 *
 * Uses takeUntilDestroyed, so call it in an injection context — a field
 * initialiser or the constructor.
 */
export function latestLoad<T>(
  load: (request: T) => Observable<unknown>,
  loading: (active: boolean) => void
): (request: T) => void {
  const requests = new Subject<T>();

  requests.pipe(
    tap(() => loading(true)),
    switchMap(request => load(request).pipe(
      map(() => true),
      defaultIfEmpty(true)
    )),
    takeUntilDestroyed()
  ).subscribe(() => loading(false));

  return request => requests.next(request);
}
